import { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';

import useChartOptions from '@/components/Chart/hooks/options/useChartOptions';
import useForwardCurveOptions from '@/components/Chart/hooks/options/useForwardCurveOptions';
import useSeasonalOptions from '@/components/Chart/hooks/options/useSeasonalOptions';
import useSpotOptions from '@/components/Chart/hooks/options/useSpotOptions';
import RegularChart from '@/components/Chart/RegularChart';
import { ChartType, ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import useUserDetails from '@/hooks/useUserDetails';

type BasicChartBlockType = {
  layout?: { fullWidth?: boolean; height?: number };
  loading: boolean;
  error: boolean;
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  chartType?: ChartType;
  buttonSection?: ReactNode;
};
const BasicChartBlock = ({
  layout,
  loading,
  error,
  chartSeries,
  chartType = 'spot',
  buttonSection
}: BasicChartBlockType) => {
  const { height } = layout || {};
  const { createOptions: createSeasonalOptions } = useSeasonalOptions(height);
  const { createOptions: createForwardCurveOptions } = useForwardCurveOptions(height);
  const { createOptions: createSpotOptions } = useSpotOptions(height);

  const getOptionsFactory = () => {
    switch (chartType) {
      case 'curve':
        return createForwardCurveOptions;
      case 'seasonal':
        return createSeasonalOptions;
      default:
        return createSpotOptions;
    }
  };
  const { options: chartOptions } = useChartOptions(
    chartSeries,

    getOptionsFactory()
  );
  const { isUserExpired } = useUserDetails();

  if (isUserExpired) {
    return (
      <Box
        sx={{
          flex: 1,
          height: '200px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ textAlign: 'center' }}>
          You no longer have access.
          <br />
          Please upgrade to access the full chart.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className='chart-container'
      sx={{ position: 'relative', minHeight: 400 }}
    >
      {buttonSection}
      <RegularChart
        showLegend
        chartSeries={chartSeries}
        chartType={chartType}
        error={error}
        height={height}
        loading={loading}
        options={chartOptions}
      />
    </Box>
  );
};

export default BasicChartBlock;
